import { useRef, useState, type ReactNode } from "react";
import { haptic } from "../platform/telegram";

/**
 * The four onboarding input components from "02 Components" (design canon):
 * memory ring, chip-input cloud, search-and-pick, selection card. Instances
 * live in OnboardingFlow; this file is the library.
 */

/* ---------------- Memory ring (O2) ---------------- */

/**
 * Progress framed as a relationship: a mint arc around FroshBot's avatar that
 * fills as he learns you, with a phase word instead of a step count. "Step 2
 * of 9" is a bounce; this is the replacement.
 */
export function MemoryRing({
  fraction,
  size = 56,
  hero = false,
}: {
  /** 0..1 — committed answers over total */
  fraction: number;
  size?: number;
  hero?: boolean;
}) {
  const stroke = hero ? 4 : 3;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const filled = Math.max(0, Math.min(1, fraction));

  return (
    <span
      className={`memring${hero ? " memring--hero" : ""}`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          className="memring__track"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          className="memring__arc"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - filled)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="memring__avatar">
        <img src="/brand/f-white-bold.png" alt="Froshbot" />
      </span>
    </span>
  );
}

/* ---------------- Chip cloud ---------------- */

/**
 * Multi-select chip cloud with optional add-your-own: the dashed chip becomes
 * an inline chip-shaped input, and `add` commits it as a selected chip — you
 * type into the crowd, not into a form field. Overflow expands in place; never
 * a second screen. No maximum: teens who tap 15 chips are telling you
 * something useful.
 */
export function ChipCloud({
  options,
  moreOptions = [],
  selected,
  onToggle,
  onAdd,
  addLabel = "your thing",
}: {
  options: string[];
  moreOptions?: string[];
  selected: string[];
  onToggle: (chip: string) => void;
  onAdd?: (chip: string) => void;
  addLabel?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const custom = selected.filter((c) => !options.includes(c) && !moreOptions.includes(c));
  const visible = expanded ? [...options, ...moreOptions] : options;

  const commitAdd = () => {
    const value = draft.trim();
    setDraft("");
    setAdding(false);
    if (value && onAdd) {
      haptic.select();
      onAdd(value);
    }
  };

  return (
    <div className="chipcloud">
      {[...visible, ...custom].map((chip) => {
        const isOn = selected.includes(chip);
        return (
          <button
            key={chip}
            type="button"
            className={`chip${isOn ? " chip--on" : ""}`}
            onClick={() => {
              haptic.select();
              onToggle(chip);
            }}
          >
            {chip}
          </button>
        );
      })}

      {onAdd ? (
        adding ? (
          <span className="chip chip--adding">
            <input
              ref={inputRef}
              className="chip__input"
              value={draft}
              placeholder={addLabel}
              autoFocus
              maxLength={40}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitAdd();
                if (e.key === "Escape") setAdding(false);
              }}
              onBlur={commitAdd}
            />
            <button type="button" className="chip__add-commit" onClick={commitAdd}>
              add
            </button>
          </span>
        ) : (
          <button type="button" className="chip chip--add" onClick={() => setAdding(true)}>
            <i className="fa-solid fa-plus" aria-hidden="true" /> {addLabel}
          </button>
        )
      ) : null}

      {!expanded && moreOptions.length > 0 ? (
        <button type="button" className="chip chip--more" onClick={() => setExpanded(true)}>
          + {moreOptions.length} more
        </button>
      ) : null}
    </div>
  );
}

/* ---------------- Search-and-pick ---------------- */

/**
 * Type-ahead over a known list with an always-present as-typed escape row —
 * the graceful fallback that stops any list from gatekeeping anyone. Tapping a
 * result commits (and closes the keyboard, because commit blurs).
 */
export function SearchPick({
  placeholder,
  results,
  onQuery,
  onPick,
  onFocusChange,
}: {
  placeholder: string;
  results: { primary: string; secondary?: string }[];
  onQuery: (q: string) => void;
  onPick: (value: string) => void;
  onFocusChange?: (focused: boolean) => void;
}) {
  const [query, setQuery] = useState("");

  const pick = (value: string) => {
    haptic.tap();
    setQuery("");
    onQuery("");
    onPick(value);
  };

  return (
    <div className="searchpick">
      <input
        className="field"
        placeholder={placeholder}
        value={query}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          onQuery(e.target.value);
        }}
        onFocus={() => onFocusChange?.(true)}
        onBlur={() => onFocusChange?.(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && query.trim()) pick(query.trim());
        }}
      />
      {query.trim() ? (
        <div className="searchpick__results">
          {results.map((r) => (
            <button
              key={r.primary}
              type="button"
              className="searchpick__row"
              // Fires before the input's blur — a tap must not lose the list.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(r.primary);
              }}
            >
              <span className="searchpick__primary">{r.primary}</span>
              {r.secondary ? <span className="searchpick__secondary">{r.secondary}</span> : null}
            </button>
          ))}
          <button
            type="button"
            className="searchpick__row searchpick__row--astyped"
            onMouseDown={(e) => {
              e.preventDefault();
              pick(query.trim());
            }}
          >
            <i className="fa-solid fa-pen" aria-hidden="true" />
            <span className="searchpick__primary">Use “{query.trim()}” as typed</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------- Selection card ---------------- */

/** Big either/or. Mint = chosen. Two of these plus chips make the prefs step
 * pure taps. */
export function SelectionCard({
  title,
  subtitle,
  selected,
  onSelect,
}: {
  title: string;
  subtitle: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`selcard${selected ? " selcard--on" : ""}`}
      onClick={() => {
        haptic.select();
        onSelect();
      }}
    >
      <span className="selcard__title">{title}</span>
      <span className="selcard__subtitle">{subtitle}</span>
    </button>
  );
}

/* ---------------- Bot bubble ---------------- */

/** Every screen is FroshBot asking one question (O1) — same bubble radii as
 * the chat (16/4), his voice hosting the flow. */
export function BotBubble({ children }: { children: ReactNode }) {
  return <div className="onb-bubble">{children}</div>;
}
